import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Monitor,
  Briefcase,
  TrendingUp,
  BarChart,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Trophy,
  RotateCcw,
  Lightbulb,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;


const CATEGORIES = ['All', 'Technology', 'Business & Management', 'Marketing', 'Data Science', 'Soft Skills'];

function getCategoryIcon(category) {
  switch (category) {
    case 'Technology': return <Monitor className="w-6 h-6" />;
    case 'Business & Management': return <Briefcase className="w-6 h-6" />;
    case 'Marketing': return <TrendingUp className="w-6 h-6" />;
    case 'Data Science': return <BarChart className="w-6 h-6" />;
    case 'Soft Skills': return <Users className="w-6 h-6" />;
    default: return <Briefcase className="w-6 h-6" />;
  }
}

function getDifficultyBadge(difficulty) {
  switch (difficulty) {
    case 'beginner':
      return <Badge className="bg-green-100 text-green-700 border-green-200">Beginner</Badge>;
    case 'intermediate':
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Intermediate</Badge>;
    case 'advanced':
      return <Badge className="bg-red-100 text-red-700 border-red-200">Advanced</Badge>;
    default:
      return <Badge variant="outline">{difficulty}</Badge>;
  }
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function SkillsTestsPage() {
  const { token, isAuthenticated } = useAuth();

  // View state: 'browse' | 'taking' | 'results'
  const [view, setView] = useState('browse');

  // Browse state
  const [tests, setTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [userResults, setUserResults] = useState([]);
  const [activeTab, setActiveTab] = useState('tests'); // 'tests' | 'my-results'

  // Taking test state
  const [activeTest, setActiveTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Results state
  const [testResult, setTestResult] = useState(null);

  // Fetch tests
  useEffect(() => {
    const fetchTests = async () => {
      setLoadingTests(true);
      try {
        const params = selectedCategory !== 'All' ? `?category=${encodeURIComponent(selectedCategory)}` : '';
        const res = await axios.get(`${API_URL}/api/skills-tests${params}`);
        setTests(res.data || []);
      } catch {
        setTests([]);
      } finally {
        setLoadingTests(false);
      }
    };
    fetchTests();
  }, [selectedCategory]);

  // Fetch user results
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    axios.get(`${API_URL}/api/skills-tests/my-results`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setUserResults(res.data || []))
      .catch(() => setUserResults([]));
  }, [isAuthenticated, token]);

  // Timer
  useEffect(() => {
    if (view !== 'taking' || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, timeLeft]);

  const handleStartTest = async (test) => {
    setActiveTest(test);
    try {
      const res = await axios.post(
        `${API_URL}/api/skills-tests/${test.id}/start`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      setQuestions(res.data?.questions || []);
    } catch {
      setQuestions([]);
    }
    setCurrentQIndex(0);
    setAnswers({});
    setTimeLeft((test.duration_minutes || 25) * 60);
    setStartTime(Date.now());
    setView('taking');
  };

  const handleSubmit = useCallback(async (autoSubmit = false) => {
    if (submitting) return;
    setSubmitting(true);
    const timeTakenMinutes = startTime
      ? Math.round((Date.now() - startTime) / 60000)
      : activeTest?.duration_minutes || 0;
    try {
      const res = await axios.post(
        `${API_URL}/api/skills-tests/${activeTest.id}/submit`,
        { answers, time_taken_minutes: timeTakenMinutes },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      setTestResult(res.data);
    } catch {
      // Fallback mock result
      const total = questions.reduce((sum, q) => sum + (q.points || 5), 0);
      const earned = Math.floor(total * 0.7);
      setTestResult({
        score: 70,
        passed: true,
        earned_points: earned,
        total_points: total,
        time_taken_minutes: timeTakenMinutes,
        passing_score: activeTest?.passing_score || 70,
        breakdown: questions.map((q, i) => ({
          question: q.question,
          user_answer_index: answers[q.id] ?? null,
          correct_index: 1,
          is_correct: answers[q.id] === 1,
          explanation: 'This is the correct answer based on standard definitions.',
          options: q.options,
        })),
      });
    } finally {
      setSubmitting(false);
      setView('results');
    }
  }, [submitting, startTime, activeTest, answers, token, questions]);

  const getUserResultForTest = (testId) => {
    return userResults.find(r => r.test_id === testId || r.test?.id === testId);
  };

  // ---- BROWSE VIEW ----
  if (view === 'browse') {
    return (
      <div className="min-h-screen bg-[#F0F4F8]">
        {/* Hero */}
        <div className="bg-[#003366] text-white py-16 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Validate Your Skills</h1>
            <p className="text-lg text-blue-200 mb-6">
              Take industry-recognised assessments and earn badges that stand out to employers.
            </p>
            <Badge className="bg-[#E11D48] text-white text-sm px-4 py-1.5">50+ Tests Available</Badge>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-10">
          {/* Tabs */}
          <div className="flex gap-2 mb-8">
            <button
              onClick={() => setActiveTab('tests')}
              className={`px-5 py-2 rounded-full font-medium text-sm transition-colors ${
                activeTab === 'tests'
                  ? 'bg-[#003366] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              Browse Tests
            </button>
            {isAuthenticated && (
              <button
                onClick={() => setActiveTab('my-results')}
                className={`px-5 py-2 rounded-full font-medium text-sm transition-colors ${
                  activeTab === 'my-results'
                    ? 'bg-[#003366] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border'
                }`}
              >
                My Results
                {userResults.length > 0 && (
                  <span className="ml-2 bg-[#E11D48] text-white text-xs rounded-full px-2 py-0.5">
                    {userResults.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {activeTab === 'tests' && (
            <>
              {/* Category Filters */}
              <div className="flex flex-wrap gap-2 mb-8">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                      selectedCategory === cat
                        ? 'bg-[#003366] text-white border-[#003366]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-[#003366] hover:text-[#003366]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Test Cards Grid */}
              {loadingTests ? (
                <div className="flex justify-center py-20">
                  <div className="w-10 h-10 border-4 border-[#003366] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tests.map(test => {
                    const result = getUserResultForTest(test.id);
                    return (
                      <Card key={test.id} className="bg-white hover:shadow-lg transition-shadow flex flex-col">
                        <CardHeader className="pb-3">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center text-[#003366] shrink-0">
                              {getCategoryIcon(test.skill_category)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base font-semibold leading-tight">{test.title}</CardTitle>
                              <p className="text-sm text-gray-500 mt-1">{test.description}</p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-3 flex-1">
                          <div className="flex flex-wrap gap-2">
                            {getDifficultyBadge(test.difficulty)}
                            <Badge variant="outline" className="text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              {test.duration_minutes} mins
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {test.questions_count} questions
                            </Badge>
                          </div>

                          {result && (
                            <div className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg ${
                              result.passed
                                ? 'bg-green-50 text-green-700'
                                : 'bg-red-50 text-red-700'
                            }`}>
                              {result.passed
                                ? <CheckCircle className="w-4 h-4" />
                                : <XCircle className="w-4 h-4" />}
                              Score: {result.score}% {result.passed ? '✓ Passed' : '✗ Failed'}
                            </div>
                          )}

                          <div className="mt-auto pt-2">
                            <Button
                              onClick={() => handleStartTest(test)}
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {result ? 'Retake Test' : 'Start Test'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeTab === 'my-results' && isAuthenticated && (
            <div className="space-y-4">
              {userResults.length === 0 ? (
                <Card className="bg-white">
                  <CardContent className="py-12 text-center">
                    <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">You haven't taken any tests yet.</p>
                    <Button
                      onClick={() => setActiveTab('tests')}
                      className="mt-4 bg-[#003366] hover:bg-[#003366]/90 text-white"
                    >
                      Browse Tests
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                userResults.map((result, i) => (
                  <Card key={i} className="bg-white">
                    <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <p className="font-semibold text-gray-900">{result.test?.title || result.test_title || 'Skills Test'}</p>
                        <p className="text-sm text-gray-500">
                          {result.completed_at ? new Date(result.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-gray-900">{result.score}%</span>
                        <Badge className={result.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {result.passed ? 'Passed' : 'Failed'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- TAKING VIEW ----
  if (view === 'taking') {
    const currentQ = questions[currentQIndex];
    const isLastQuestion = currentQIndex === questions.length - 1;
    const isTimeLow = timeLeft < 60;

    return (
      <div className="min-h-screen bg-[#F0F4F8]">
        {/* Test Header */}
        <div className="bg-white border-b sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-[#003366] text-lg">{activeTest?.title}</h2>
              <p className="text-sm text-gray-500">
                Question {currentQIndex + 1} of {questions.length}
              </p>
            </div>
            <div className={`flex items-center gap-2 text-lg font-mono font-bold px-4 py-2 rounded-lg ${
              isTimeLow ? 'bg-red-50 text-red-600' : 'bg-[#003366]/10 text-[#003366]'
            }`}>
              <Clock className="w-5 h-5" />
              {formatTime(timeLeft)}
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-gray-100">
            <div
              className="h-full bg-[#003366] transition-all"
              style={{ width: `${((currentQIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {isTimeLow && (
          <div className="max-w-3xl mx-auto px-4 pt-4">
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Less than 60 seconds remaining! The test will submit automatically.
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto px-4 py-8">
          <Card className="bg-white shadow-sm">
            <CardContent className="p-6 md:p-8">
              {currentQ && (
                <>
                  <div className="mb-6">
                    <span className="text-sm font-medium text-gray-400 uppercase tracking-wide">
                      Question {currentQIndex + 1}
                    </span>
                    <p className="text-xl font-medium text-gray-900 mt-2 leading-relaxed">
                      {currentQ.question}
                    </p>
                  </div>

                  <div className="space-y-3">
                    {currentQ.options.map((option, idx) => {
                      const isSelected = answers[currentQ.id] === idx;
                      const letter = ['A', 'B', 'C', 'D'][idx];
                      return (
                        <button
                          key={idx}
                          onClick={() => setAnswers(prev => ({ ...prev, [currentQ.id]: idx }))}
                          className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-800'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                            isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {letter}
                          </span>
                          <span className="text-sm md:text-base">{option}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentQIndex(i => Math.max(0, i - 1))}
              disabled={currentQIndex === 0}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                onClick={() => handleSubmit(false)}
                disabled={submitting}
                className="bg-[#E11D48] hover:bg-[#E11D48]/90 text-white flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Test'
                )}
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentQIndex(i => Math.min(questions.length - 1, i + 1))}
                className="bg-[#003366] hover:bg-[#003366]/90 text-white flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Question dots */}
          <div className="flex flex-wrap gap-2 mt-6 justify-center">
            {questions.map((q, i) => (
              <button
                key={i}
                onClick={() => setCurrentQIndex(i)}
                className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                  i === currentQIndex
                    ? 'bg-[#003366] text-white'
                    : answers[q.id] !== undefined
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : 'bg-gray-100 text-gray-500 border border-gray-200'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---- RESULTS VIEW ----
  if (view === 'results' && testResult) {
    const passed = testResult.passed;
    const score = testResult.score;

    return (
      <div className="min-h-screen bg-[#F0F4F8] py-10 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Score Card */}
          <Card className="bg-white shadow-sm text-center">
            <CardContent className="p-8">
              <div className={`w-36 h-36 rounded-full border-8 flex items-center justify-center mx-auto mb-6 ${
                passed ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'
              }`}>
                <span className={`text-4xl font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>
                  {score}%
                </span>
              </div>

              <Badge className={`text-lg px-6 py-2 mb-4 ${
                passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {passed ? 'PASSED' : 'FAILED'}
              </Badge>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">{activeTest?.title}</h2>
              <p className="text-gray-500">
                {passed ? 'Congratulations! You passed this assessment.' : 'Keep practising and try again!'}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-gray-900">{testResult.earned_points}</p>
                  <p className="text-xs text-gray-500 mt-1">Points Earned</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-gray-900">{testResult.total_points}</p>
                  <p className="text-xs text-gray-500 mt-1">Total Points</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-gray-900">{testResult.time_taken_minutes}</p>
                  <p className="text-xs text-gray-500 mt-1">Minutes Taken</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-gray-900">{testResult.passing_score}%</p>
                  <p className="text-xs text-gray-500 mt-1">Passing Score</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
                <Button
                  onClick={() => {
                    setView('browse');
                    setActiveTest(null);
                    setTestResult(null);
                    setQuestions([]);
                    setAnswers({});
                  }}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Take Another Test
                </Button>
                <Link to="/profile">
                  <Button className="bg-[#003366] hover:bg-[#003366]/90 text-white w-full sm:w-auto">
                    View My Profile
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Breakdown */}
          {testResult.breakdown && testResult.breakdown.length > 0 && (
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Question Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {testResult.breakdown.map((item, i) => (
                  <div
                    key={i}
                    className={`p-4 rounded-xl border-l-4 ${
                      item.is_correct ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'
                    }`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {item.is_correct
                        ? <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                        : <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
                      <p className="font-medium text-gray-900 text-sm">{item.question}</p>
                    </div>
                    <div className="ml-7 space-y-1 text-sm">
                      {item.user_answer_index !== null && item.options && (
                        <p className={item.is_correct ? 'text-green-700' : 'text-red-700'}>
                          Your answer: {item.options[item.user_answer_index]}
                        </p>
                      )}
                      {!item.is_correct && item.options && item.correct_index !== undefined && (
                        <p className="text-green-700">
                          Correct answer: {item.options[item.correct_index]}
                        </p>
                      )}
                      {item.explanation && (
                        <p className="text-gray-600 mt-2 flex items-start gap-1.5">
                          <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                          {item.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return null;
}
